import {prettifyEnum} from 'utils/currencyFormatting';
import {enabledPaymentMethod} from 'utils/mock';

/**
 * Per-method deposit/withdrawal configuration for a wallet currency.
 *
 * The service stores an array of `paymentMethodConfigs` on each direction, one
 * entry per payment method, carrying the localised strings the app shows and
 * the country list that hides the method. This module owns the translation
 * between that array and the flat shape the form edits.
 *
 * Two rules from the service contract drive nearly everything here:
 *
 *   1. Sending `paymentMethodConfigs` REPLACES the stored array — it does not
 *      merge. Whatever the form submits is the whole truth for that direction,
 *      so `buildPaymentMethodConfigs` always emits every method worth keeping,
 *      never just the one that was edited.
 *   2. An explicit `[]` clears every config, and is deliberately different
 *      from omitting the key (which makes the service carry the stored value
 *      forward). The form always knows about the field, so it always sends it
 *      — the carry-forward is a guard for older forms, not a merge to lean on.
 */

/**
 * A method's human label — never the raw enum.
 *
 * Falls back to un-shouting the enum for a method the admin's option list does
 * not know about, so a new backend value still reads as words rather than
 * WIRE_TRANSFER.
 */
export const methodLabel = method =>
  enabledPaymentMethod.find(entry => entry.key === method)?.value ||
  prettifyEnum(method);

/** The only languages the service accepts for a localised string. */
export const CONFIG_LANGUAGES = [
  {key: 'EN', label: 'English'},
  {key: 'FR', label: 'French'},
];

/** The localised fields, in the order they read on screen. */
export const LOCALIZED_FIELDS = [
  {
    key: 'displayName',
    label: 'Display name',
    help: 'Overrides the method’s label. Leave blank to use the app’s own.',
  },
  {
    key: 'subText',
    label: 'Sub text',
    help: 'The line under the method — “Takes a few minutes”, “2 - 4 Business days”.',
  },
  {
    key: 'feeText',
    label: 'Fee text',
    help: 'Overrides “Fees apply”.',
  },
];

/** A stored `[{language, text}]` array as `{EN, FR}` for the form. */
export const localizedToForm = (entries = []) =>
  (entries || []).reduce((acc, entry) => {
    if (entry?.language) acc[entry.language] = entry.text ?? '';
    return acc;
  }, {});

/**
 * `{EN, FR}` back to `[{language, text}]`, dropping blanks.
 *
 * An empty string is not a translation — it is "no override", and the app
 * falls back to its bundled string. Sending `{language: 'FR', text: ''}` would
 * persist an empty override that blanks the line in the app instead.
 */
export const formToLocalized = (values = {}) =>
  CONFIG_LANGUAGES.map(({key}) => ({
    language: key,
    text: String(values?.[key] ?? '').trim(),
  })).filter(entry => entry.text);

/** One method's stored config as the flat shape a row edits. */
export const configToForm = (config = {}) => ({
  enabled: config?.enabled !== false,
  available: config?.available !== false,
  order:
    typeof config?.order === 'number' && Number.isFinite(config.order)
      ? config.order
      : undefined,
  logoUrl: config?.logoUrl ?? '',
  restrictedCountries: config?.restrictedCountries ?? [],
  displayName: localizedToForm(config?.displayName),
  subText: localizedToForm(config?.subText),
  feeText: localizedToForm(config?.feeText),
});

/**
 * Every stored config for a direction, keyed by method.
 *
 * Keyed rather than kept as a list because the editor addresses rows by
 * method, and because a keyed lookup silently collapses a duplicate `method`
 * the way the read path does — it takes the first entry and ignores the rest.
 */
export const configsToFormState = (configs = []) =>
  (configs || []).reduce((acc, config) => {
    if (config?.method && !acc[config.method]) {
      acc[config.method] = configToForm(config);
    }
    return acc;
  }, {});

/** An untouched row: enabled, with nothing overridden. */
export const emptyConfigForm = () => ({
  enabled: true,
  available: true,
  order: undefined,
  logoUrl: '',
  restrictedCountries: [],
  displayName: {},
  subText: {},
  feeText: {},
});

/**
 * Whether a row says anything worth storing.
 *
 * A method with no config renders an empty row, and saving it blank must be a
 * no-op rather than writing `{method, enabled: true}` for every enabled method
 * on the currency. `enabled: true` is the default, so only an explicit
 * `false` counts as content.
 */
/**
 * Whether the operator has actually overridden anything about this method.
 *
 * Deliberately excludes `order`: since reordering numbers every row at once,
 * counting position as "configured" made all eight rows claim an override
 * after a single drag. Position is a property of the list, not of the method.
 */
export const hasMethodOverrides = (form = {}) =>
  form.enabled === false ||
  form.available === false ||
  Boolean(String(form.logoUrl ?? '').trim()) ||
  (form.restrictedCountries || []).length > 0 ||
  LOCALIZED_FIELDS.some(({key}) => formToLocalized(form[key] ?? {}).length > 0);

export const hasConfigContent = (form = {}) =>
  form.enabled === false ||
  form.available === false ||
  typeof form.order === 'number' ||
  Boolean(String(form.logoUrl ?? '').trim()) ||
  (form.restrictedCountries || []).length > 0 ||
  LOCALIZED_FIELDS.some(({key}) => formToLocalized(form[key] ?? {}).length > 0);

/**
 * The `paymentMethodConfigs` array for one direction.
 *
 * `methods` is `enabledPaymentMethods` — the rows on screen — so a method
 * removed from that list drops its config, and one added to it gets a row.
 * The method list is de-duplicated first: a duplicate `method` is rejected by
 * the service, and the read path would silently ignore the second entry
 * anyway, so it must be impossible to produce.
 *
 * Returns `[]` when nothing is configured, which is the clear-everything
 * signal rather than an absence of one.
 */
export const buildPaymentMethodConfigs = (methods = [], state = {}) => {
  const seen = {};

  return (methods || [])
    .filter(method => {
      if (!method || seen[method]) return false;
      seen[method] = true;
      return true;
    })
    .reduce((acc, method) => {
      const form = state[method];
      if (!form || !hasConfigContent(form)) return acc;

      const displayName = formToLocalized(form.displayName);
      const subText = formToLocalized(form.subText);
      const feeText = formToLocalized(form.feeText);
      const logoUrl = String(form.logoUrl ?? '').trim();
      const countries = normalizeCountries(form.restrictedCountries);

      acc.push({
        method,
        enabled: form.enabled !== false,
        available: form.available !== false,
        // Every optional field is omitted rather than sent empty: the service
        // treats an absent key as "no override" and a present one as a value
        // to store.
        ...(typeof form.order === 'number' && {order: form.order}),
        ...(logoUrl && {logoUrl}),
        ...(countries.length && {restrictedCountries: countries}),
        ...(displayName.length && {displayName}),
        ...(subText.length && {subText}),
        ...(feeText.length && {feeText}),
      });
      return acc;
    }, []);
};

/**
 * ISO-2, uppercase, de-duplicated.
 *
 * Stored codes are compared against the user's `countryISO`, which is always
 * alpha-2, so a three-letter code would save cleanly and then match nobody.
 * The picker only offers alpha-2, but this also covers a value restored from
 * an older record.
 */
export const normalizeCountries = (countries = []) => {
  const seen = {};
  return (countries || [])
    .map(code =>
      String(code ?? '')
        .trim()
        .toUpperCase(),
    )
    .filter(code => {
      if (code.length !== 2 || seen[code]) return false;
      seen[code] = true;
      return true;
    });
};

/**
 * The first problem that would come back from the service as a save failure,
 * or null.
 *
 * Checked here because these surface as a generic mutation error against the
 * whole currency, with nothing tying them to the method that caused them.
 */
export const findConfigError = (methods = [], state = {}, directionLabel) => {
  const configs = buildPaymentMethodConfigs(methods, state);

  const badUrl = configs.find(
    config => config.logoUrl && !isUrl(config.logoUrl),
  );
  if (badUrl) {
    return `${directionLabel}: “${badUrl.logoUrl}” is not a valid logo URL for ${badUrl.method}.`;
  }

  const badCountry = (methods || []).reduce((found, method) => {
    if (found) return found;
    const raw = state[method]?.restrictedCountries || [];
    const bad = raw.find(code => String(code ?? '').trim().length !== 2);
    return bad ? {method, bad} : null;
  }, null);
  if (badCountry) {
    return `${directionLabel}: “${badCountry.bad}” is not a 2-letter country code for ${badCountry.method}.`;
  }

  return null;
};

const isUrl = value => {
  try {
    // eslint-disable-next-line no-undef
    new URL(value);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * A one-line description of a direction's method setup, for the confirm modal.
 *
 * The stored shape is per-method, per-field, per-language; printing it whole
 * would bury the single line the operator actually changed.
 */
export const summariseMethodConfigs = (methods = [], state = {}) => {
  const configs = buildPaymentMethodConfigs(methods, state);
  if (!configs.length) return 'App defaults for every method';

  return (
    configs
      .map(config => {
        const notes = [];
        if (config.enabled === false) notes.push('hidden');
        if (config.available === false) notes.push('unavailable');
        // Shown one-based: the operator sees a list, not a zero-indexed array.
        if (typeof config.order === 'number') {
          notes.push(`position ${config.order + 1}`);
        }
        if (config.displayName) notes.push('name');
        if (config.subText) notes.push('sub text');
        if (config.feeText) notes.push('fee text');
        if (config.logoUrl) notes.push('logo');
        if (config.restrictedCountries) {
          notes.push(`blocked in ${config.restrictedCountries.join(', ')}`);
        }
        return `${methodLabel(config.method)}: ${notes.join(', ')}`;
      })
      // One per line; the modal renders it with `white-space: pre-line`. Joined
      // inline it ran into an unreadable wall for a currency with six methods.
      .join('\n')
  );
};

/**
 * The methods for a direction in the order they will appear in the app.
 *
 * Anything with an explicit `order` leads, ascending; the rest keep the order
 * of `enabledPaymentMethods` behind them. That second part matters on a
 * currency nobody has reordered yet — without it the list would shuffle on
 * every render and dragging would fight the operator.
 */
export const orderMethods = (methods = [], state = {}) => {
  const rows = (methods || []).filter(
    (method, index, all) => method && all.indexOf(method) === index,
  );

  return rows
    .map((method, index) => ({method, index, order: state[method]?.order}))
    .sort((a, b) => {
      const left = typeof a.order === 'number' ? a.order : Infinity;
      const right = typeof b.order === 'number' ? b.order : Infinity;
      if (left !== right) return left - right;
      return a.index - b.index;
    })
    .map(entry => entry.method);
};

/**
 * Writes the list's own positions back as `order`.
 *
 * Every visible method is numbered from its index, which is what makes a
 * duplicate position impossible — the old number box let an operator type 1
 * on two different rows, and the app would then order them arbitrarily.
 *
 * It does mean one drag gives every method in that direction an explicit
 * order, which is accurate: an ordering is a statement about the whole list,
 * not about the row that moved.
 */
export const applyMethodOrder = (orderedMethods = [], state = {}) =>
  orderedMethods.reduce(
    (acc, method, index) => ({
      ...acc,
      [method]: {...emptyConfigForm(), ...(state[method] || {}), order: index},
    }),
    {...state},
  );
